import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  ArrowUpDown,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Check,
  Loader2,
  MinusCircle,
  X,
  XCircle,
} from 'lucide-react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Layout } from '@/components/Layout'
import { getEAP, getEAPCompliance } from '@/services/eap.service'
import { cn, uniqueEmails } from '@/utils/helpers'

const StatusBadge = ({ status }) => {
  const config = {
    compliant: {
      label: 'Compliant',
      variant: 'success',
      icon: CheckCircle,
    },
    overdue: {
      label: 'Overdue',
      variant: 'destructive',
      icon: XCircle,
    },
    never_completed: {
      label: 'Never Completed',
      variant: 'warning',
      icon: Clock,
    },
    not_required: {
      label: 'Not Required',
      variant: 'secondary',
      icon: MinusCircle,
    },
  }

  const { label, variant, icon: Icon } = config[status] || config.never_completed

  return (
    <Badge variant={variant} className="flex items-center gap-1">
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  )
}

StatusBadge.propTypes = {
  status: PropTypes.oneOf(['compliant', 'overdue', 'never_completed', 'not_required']),
}

const SortIcon = ({ sortKey, currentSort }) => {
  if (currentSort.key !== sortKey) {
    return <ArrowUpDown className="ml-1 inline h-3 w-3 text-gray-400" />
  }
  if (currentSort.direction === 'asc') {
    return <ChevronUp className="ml-1 inline h-3 w-3" />
  }
  return <ChevronDown className="ml-1 inline h-3 w-3" />
}

SortIcon.propTypes = {
  sortKey: PropTypes.string.isRequired,
  currentSort: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc']),
  }).isRequired,
}

export const EAPCompliance = observer(() => {
  const { entityId, eapId } = useParams()
  const navigate = useNavigate()
  const [sort, setSort] = useState({ key: 'status', direction: 'asc' })
  const [statusFilter, setStatusFilter] = useState(null)
  const [emailsCopied, setEmailsCopied] = useState(false)

  // GET EAP details
  const {
    data: eapData,
    isPending: isLoadingEAP,
    error: eapError,
  } = useQuery({
    queryKey: ['accounts', 'eaps', entityId, eapId],
    queryFn: () => getEAP({ entityId, eapId }),
  })

  // GET Compliance data
  const {
    data: complianceData,
    isPending: isLoadingCompliance,
    error: complianceError,
  } = useQuery({
    queryKey: ['accounts', 'eaps', entityId, eapId, 'compliance'],
    queryFn: () => getEAPCompliance({ entityId, eapId }),
    enabled: !!eapData?.quizEnabled,
  })

  const handleBackToEAPs = () => {
    navigate(`/accounts/${entityId}/eaps`)
  }

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handleFilterByStatus = (status) => {
    setStatusFilter((prev) => (prev === status ? null : status))
  }

  const outOfComplianceEmails = useMemo(() => {
    if (!complianceData?.users) return ''
    return uniqueEmails(
      complianceData.users
        .filter((u) => u.status === 'overdue' || u.status === 'never_completed')
        .map((u) => u.userEmail),
    ).join(', ')
  }, [complianceData?.users])

  const handleCopyEmails = () => {
    if (!outOfComplianceEmails) return
    navigator.clipboard.writeText(outOfComplianceEmails).then(() => {
      setEmailsCopied(true)
      setTimeout(() => setEmailsCopied(false), 2000)
    })
  }

  const sortedUsers = useMemo(() => {
    if (!complianceData?.users) return []

    let users
    if (statusFilter === 'required') {
      users = complianceData.users.filter((u) => u.status !== 'not_required')
    } else if (statusFilter) {
      users = complianceData.users.filter((u) => u.status === statusFilter)
    } else {
      users = [...complianceData.users]
    }
    const { key, direction } = sort
    const multiplier = direction === 'asc' ? 1 : -1

    // Status priority for sorting
    const statusPriority = { overdue: 0, never_completed: 1, compliant: 2, not_required: 3 }

    users.sort((a, b) => {
      let comparison = 0

      if (key === 'user') {
        comparison = (a.userName || '').localeCompare(b.userName || '')
      } else if (key === 'status') {
        comparison = (statusPriority[a.status] ?? 4) - (statusPriority[b.status] ?? 4)
      } else if (key === 'lastAttempt') {
        const dateA = a.lastAttempt?.date ? new Date(a.lastAttempt.date).getTime() : 0
        const dateB = b.lastAttempt?.date ? new Date(b.lastAttempt.date).getTime() : 0
        comparison = dateA - dateB
      } else if (key === 'score') {
        const scoreA =
          a.lastAttempt && a.lastAttempt.totalQuestions > 0
            ? a.lastAttempt.score / a.lastAttempt.totalQuestions
            : -1
        const scoreB =
          b.lastAttempt && b.lastAttempt.totalQuestions > 0
            ? b.lastAttempt.score / b.lastAttempt.totalQuestions
            : -1
        comparison = scoreA - scoreB
      } else if (key === 'nextDueDate') {
        const dateA = a.nextDueDate ? new Date(a.nextDueDate).getTime() : Infinity
        const dateB = b.nextDueDate ? new Date(b.nextDueDate).getTime() : Infinity
        comparison = dateA - dateB
      }

      return comparison * multiplier
    })

    return users
  }, [complianceData?.users, sort, statusFilter])

  if (isLoadingEAP || isLoadingCompliance) {
    return (
      <Layout
        breadcrumbs={[
          'Admin',
          { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
          'Compliance',
        ]}
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
        </div>
      </Layout>
    )
  }

  if (eapError) {
    return (
      <Layout
        breadcrumbs={[
          'Admin',
          { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
          'Compliance',
        ]}
      >
        <div className="flex flex-col items-center justify-center py-12">
          <p className="mb-4 text-gray-600">Unable to load EAP. Please try again.</p>
          <Button onClick={handleBackToEAPs}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to EAPs
          </Button>
        </div>
      </Layout>
    )
  }

  if (!eapData?.quizEnabled) {
    return (
      <Layout
        breadcrumbs={[
          'Admin',
          { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
          'Compliance',
        ]}
      >
        <div className="flex flex-col items-center justify-center py-12">
          <p className="mb-4 text-gray-600">Quiz is not enabled for this EAP.</p>
          <Button onClick={handleBackToEAPs}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to EAPs
          </Button>
        </div>
      </Layout>
    )
  }

  if (complianceError) {
    return (
      <Layout
        breadcrumbs={[
          'Admin',
          { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
          'Compliance',
        ]}
      >
        <div className="flex flex-col items-center justify-center py-12">
          <p className="mb-4 text-gray-600">Unable to load compliance data.</p>
          <Button onClick={handleBackToEAPs}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to EAPs
          </Button>
        </div>
      </Layout>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <Layout
      breadcrumbs={[
        'Admin',
        { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
        eapData?.name || 'Compliance',
      ]}
    >
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Button variant="ghost" onClick={handleBackToEAPs}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-gray-900">
            {eapData?.name} - Quiz Compliance
          </h1>
          <p className="text-sm text-gray-500">{complianceData?.quizQuestionCount} questions</p>
        </div>
        {outOfComplianceEmails && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyEmails}
            className="flex items-center gap-1.5"
          >
            {emailsCopied ? (
              <>
                <Check className="h-4 w-4 text-green-600" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy out-of-compliance emails
              </>
            )}
          </Button>
        )}
      </div>

      {/* Job Restrictions Info & Active Filter */}
      {(complianceData?.eapJobs?.length > 0 || statusFilter) && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          {complianceData?.eapJobs?.length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm text-blue-700">
                <span className="font-medium">Required for jobs:</span>{' '}
                {complianceData.eapJobs.map((j) => j.title).join(', ')}
              </p>
            </div>
          )}
          {statusFilter && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Filtered by:</span>
              <Badge variant="secondary" className="flex items-center gap-1">
                {statusFilter === 'required' ? 'Required Users' : null}
                {statusFilter === 'compliant' ? 'Compliant' : null}
                {statusFilter === 'overdue' ? 'Overdue' : null}
                {statusFilter === 'never_completed' ? 'Never Completed' : null}
                {statusFilter === 'not_required' ? 'Not Required' : null}
                <button
                  type="button"
                  onClick={() => setStatusFilter(null)}
                  className="ml-1 rounded-full p-0.5 hover:bg-gray-300"
                  aria-label="Clear filter"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-5">
        <button
          type="button"
          className={cn(
            'rounded-lg border p-4 text-left transition-all hover:ring-2 hover:ring-gray-300',
            statusFilter === 'required'
              ? 'border-gray-400 bg-white ring-2 ring-gray-400'
              : 'border-gray-200 bg-white',
          )}
          aria-label={`Required Users: ${complianceData?.requiredUsers || 0}. Click to filter.`}
          aria-pressed={statusFilter === 'required'}
          onClick={() => handleFilterByStatus('required')}
        >
          <div className="text-2xl font-bold text-gray-900">
            {complianceData?.requiredUsers || 0}
          </div>
          <div className="text-sm text-gray-500">Required Users</div>
        </button>
        <button
          type="button"
          className={cn(
            'rounded-lg border p-4 text-left transition-all hover:ring-2 hover:ring-green-300',
            statusFilter === 'compliant'
              ? 'border-green-400 bg-green-50 ring-2 ring-green-400'
              : 'border-green-200 bg-green-50',
          )}
          aria-label={`Compliant: ${complianceData?.compliantCount || 0}. Click to filter.`}
          aria-pressed={statusFilter === 'compliant'}
          onClick={() => handleFilterByStatus('compliant')}
        >
          <div className="text-2xl font-bold text-green-700">
            {complianceData?.compliantCount || 0}
          </div>
          <div className="text-sm text-green-600">Compliant</div>
        </button>
        <button
          type="button"
          className={cn(
            'rounded-lg border p-4 text-left transition-all hover:ring-2 hover:ring-red-300',
            statusFilter === 'overdue'
              ? 'border-red-400 bg-red-50 ring-2 ring-red-400'
              : 'border-red-200 bg-red-50',
          )}
          aria-label={`Overdue: ${complianceData?.overdueCount || 0}. Click to filter.`}
          aria-pressed={statusFilter === 'overdue'}
          onClick={() => handleFilterByStatus('overdue')}
        >
          <div className="text-2xl font-bold text-red-700">
            {complianceData?.overdueCount || 0}
          </div>
          <div className="text-sm text-red-600">Overdue</div>
        </button>
        <button
          type="button"
          className={cn(
            'rounded-lg border p-4 text-left transition-all hover:ring-2 hover:ring-yellow-300',
            statusFilter === 'never_completed'
              ? 'border-yellow-400 bg-yellow-50 ring-2 ring-yellow-400'
              : 'border-yellow-200 bg-yellow-50',
          )}
          aria-label={`Never Completed: ${complianceData?.neverCompletedCount || 0}. Click to filter.`}
          aria-pressed={statusFilter === 'never_completed'}
          onClick={() => handleFilterByStatus('never_completed')}
        >
          <div className="text-2xl font-bold text-yellow-700">
            {complianceData?.neverCompletedCount || 0}
          </div>
          <div className="text-sm text-yellow-600">Never Completed</div>
        </button>
        <button
          type="button"
          className={cn(
            'rounded-lg border p-4 text-left transition-all hover:ring-2 hover:ring-gray-300',
            statusFilter === 'not_required'
              ? 'border-gray-400 bg-gray-50 ring-2 ring-gray-400'
              : 'border-gray-200 bg-gray-50',
          )}
          aria-label={`Not Required: ${complianceData?.notRequiredCount || 0}. Click to filter.`}
          aria-pressed={statusFilter === 'not_required'}
          onClick={() => handleFilterByStatus('not_required')}
        >
          <div className="text-2xl font-bold text-gray-500">
            {complianceData?.notRequiredCount || 0}
          </div>
          <div className="text-sm text-gray-500">Not Required</div>
        </button>
      </div>

      {/* Users Table */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th
                  className="cursor-pointer px-4 py-3 text-left text-sm font-medium text-gray-600 hover:bg-gray-100"
                  onClick={() => handleSort('user')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleSort('user')
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Sort by user"
                >
                  User
                  <SortIcon sortKey="user" currentSort={sort} />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Roles</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Jobs</th>
                <th
                  className="cursor-pointer px-4 py-3 text-left text-sm font-medium text-gray-600 hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleSort('status')
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Sort by status"
                >
                  Status
                  <SortIcon sortKey="status" currentSort={sort} />
                </th>
                <th
                  className="cursor-pointer px-4 py-3 text-left text-sm font-medium text-gray-600 hover:bg-gray-100"
                  onClick={() => handleSort('lastAttempt')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleSort('lastAttempt')
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Sort by last attempt"
                >
                  Last Attempt
                  <SortIcon sortKey="lastAttempt" currentSort={sort} />
                </th>
                <th
                  className="cursor-pointer px-4 py-3 text-left text-sm font-medium text-gray-600 hover:bg-gray-100"
                  onClick={() => handleSort('nextDueDate')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleSort('nextDueDate')
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Sort by next due date"
                >
                  Next Due Date
                  <SortIcon sortKey="nextDueDate" currentSort={sort} />
                </th>
                <th
                  className="cursor-pointer px-4 py-3 text-left text-sm font-medium text-gray-600 hover:bg-gray-100"
                  onClick={() => handleSort('score')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleSort('score')
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Sort by score"
                >
                  Latest Score
                  <SortIcon sortKey="score" currentSort={sort} />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedUsers.map((user) => (
                <tr
                  key={user.userId}
                  className={cn(
                    user.status === 'overdue' && 'bg-red-50',
                    user.status === 'never_completed' && 'bg-yellow-50',
                  )}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{user.userName}</div>
                    <div className="text-sm text-gray-500">{user.userEmail}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {user.roles?.map((role) => (
                        <Badge key={role} variant="outline" className="text-xs">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {user.jobs?.length > 0 ? (
                        user.jobs.map((job) => (
                          <Badge key={job.id} variant="outline" className="text-xs">
                            {job.title}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={user.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {user.lastAttempt ? (
                      <div>
                        <div>{formatDate(user.lastAttempt.date)}</div>
                        <div
                          className={cn(
                            'text-xs',
                            user.lastAttempt.result === 'passed'
                              ? 'text-green-600'
                              : 'text-red-600',
                          )}
                        >
                          {user.lastAttempt.result === 'passed' ? 'Passed' : 'Failed'}
                        </div>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(user.nextDueDate)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {user.lastAttempt
                      ? `${user.lastAttempt.score}/${user.lastAttempt.totalQuestions}`
                      : '-'}
                  </td>
                </tr>
              ))}
              {sortedUsers.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    {statusFilter
                      ? 'No users match the selected filter.'
                      : 'No users have access to this EAP.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  )
})
